import { isDiscardingChanges } from "./leaveGuardStore";
import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, Modal, ModalDialog } from "@mui/joy";
import { useLocation } from "react-router-dom";

import { usePlatform } from "../platform/PlatformProvider";
import { browserNavigationTarget } from "../utils/tenantRoute";
import {
    discardAndLeave,
    hasUnsavedChanges,
    holdUnsavedChanges,
    keepEditing,
    requestLeave,
    useLeaveGuardStore,
    type LeavePrompt,
    type LeaveRequest,
} from "./leaveGuardStore";
import { useNavBarTabId } from "./NavBarScope";

/**
 * The one question before unsaved changes are lost. A page reports its unsaved changes
 * (`useUnsavedChangesPrompt`); whatever can take the page away -- a rail, a header link, a
 * page's own left nav or crumbs, a desktop tab's navigation -- asks here first
 * (`requestLeave`): with nothing unsaved it goes at once, otherwise a "Leave without saving?"
 * dialog decides, and it goes only on Discard changes. Holds belong to the tab the page is in
 * (`NavBarScope`; the web window is one tab), so a desktop tab kept alive behind another never
 * holds up the one in front. The dialog renders in a `LeaveGuardHost`: the app and each page
 * that can ask mount one, and only the first mounted draws it.
 */

export {
    hasUnsavedChanges,
    holdUnsavedChanges,
    lostMessage,
    requestLeave,
    type LeavePrompt,
    type LeaveRequest,
} from "./leaveGuardStore";

/** The dialog itself: "Leave without saving?", what is lost, Discard changes or Keep editing,
 *  unless the page holding the leave asked its own question (`prompt`). */
function LeaveDialog({
    open,
    message,
    prompt,
    onDiscard,
    onKeep,
}: {
    open: boolean;
    /** What would be lost. */
    message: ReactNode;
    prompt?: LeavePrompt;
    onDiscard: () => void;
    onKeep: () => void;
}) {
    return (
        <Modal open={open} onClose={onKeep}>
            <ModalDialog role="alertdialog" aria-labelledby="leave-guard-title">
                <DialogTitle id="leave-guard-title">
                    {prompt?.title ?? "Leave without saving?"}
                </DialogTitle>
                <DialogContent>{message}</DialogContent>
                <DialogActions>
                    <Button color="danger" onClick={onDiscard}>
                        {prompt?.leaveLabel ?? "Discard changes"}
                    </Button>
                    <Button variant="soft" color="neutral" onClick={onKeep}>
                        {prompt?.stayLabel ?? "Keep editing"}
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    );
}

/**
 * Where the guard's dialog renders. Mount one wherever a page can hold changes or chrome can
 * ask; however many are mounted, only the first draws the dialog. When the last one unmounts,
 * a question still open goes with it.
 */
export function LeaveGuardHost() {
    const [id] = useState(() => Symbol("leave-guard-host"));
    useEffect(() => {
        useLeaveGuardStore.setState(({ hosts }) => ({ hosts: [...hosts, id] }));
        return () =>
            useLeaveGuardStore.setState(({ hosts, pending }) => {
                const rest = hosts.filter((host) => host !== id);
                return { hosts: rest, pending: rest.length === 0 ? null : pending };
            });
    }, [id]);
    const drawn = useLeaveGuardStore((state) => state.hosts[0] === id);
    const pending = useLeaveGuardStore((state) => state.pending);
    if (!drawn) return null;
    return (
        <LeaveDialog
            open={pending !== null}
            message={pending?.message}
            prompt={pending?.prompt}
            onKeep={keepEditing}
            onDiscard={discardAndLeave}
        />
    );
}

/**
 * Reports a page's unsaved changes: how many, or only whether there are any. While there are,
 * closing or reloading the browser tab asks first, and so does anything that leaves the page
 * through the guard.
 */
export function useUnsavedChangesPrompt(changes: number | boolean, prompt?: LeavePrompt) {
    const scope = useNavBarTabId();
    const count = typeof changes === "number" ? changes : undefined;
    const dirty = typeof changes === "number" ? changes > 0 : changes;
    // Read when a leave is asked, so the question says what is true then.
    const promptRef = useRef(prompt);
    promptRef.current = prompt;
    const asks = prompt !== undefined;
    useEffect(() => {
        if (!dirty) return;
        return holdUnsavedChanges(
            scope,
            count,
            asks ? () => promptRef.current as LeavePrompt : undefined,
        );
    }, [dirty, count, scope, asks]);
    useBeforeUnloadPrompt(dirty);
}

/**
 * While `dirty`, closing or reloading the browser tab asks first. Alone, it is for a page whose
 * in-app leave saves on the way out; `useUnsavedChangesPrompt` adds the in-app question.
 */
export function useBeforeUnloadPrompt(dirty: boolean) {
    useEffect(() => {
        if (!dirty) return;
        const warn = (event: BeforeUnloadEvent) => {
            if (isDiscardingChanges()) return;
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);
}

/**
 * `leave(go, request)`: `requestLeave` for the tab this component is in, unless the request
 * names another.
 */
export function useLeaveGuard(): (go: () => void, request?: LeaveRequest) => void {
    const scope = useNavBarTabId();
    return useCallback(
        (go: () => void, request: LeaveRequest = {}) => requestLeave(go, { scope, ...request }),
        [scope],
    );
}

/**
 * The router's `navigate` for a host's `open` (the web's `installNavigateHandler`): a move to
 * another address asks first while the page shown has unsaved changes, so every same-tab open
 * through the platform -- the rail, a link a page opens -- goes through the guard. A move to
 * the address already shown goes at once.
 */
export function useLeaveGuardedNavigate(navigate: (path: string) => void): (path: string) => void {
    const scope = useNavBarTabId();
    const { pathname, search } = useLocation();
    const here = useRef(`${pathname}${search}`);
    here.current = `${pathname}${search}`;
    return useCallback(
        (path: string) => {
            const target = browserNavigationTarget(path).routerPath;
            // Router's location omits its basename. Compare within the same
            // admitted project while still passing the explicit destination on.
            if (target !== null && target.split("#", 1)[0] === here.current) navigate(path);
            else requestLeave(() => navigate(path), { scope });
        },
        [navigate, scope],
    );
}

/**
 * A click-capture handler for chrome made of links (the web header). A plain click on an
 * in-app link to another address, while the page has unsaved changes, asks first and follows
 * the link in the app once they are discarded. A new-tab click, a link to another site or
 * window, a download and a link to the address already shown go their own way.
 */
function useLeaveGuardLinks(): (event: MouseEvent) => void {
    const platform = usePlatform();
    const scope = useNavBarTabId();
    const { pathname, search } = useLocation();
    return useCallback(
        (event: MouseEvent) => {
            if (!hasUnsavedChanges(scope) || event.defaultPrevented) return;
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
            if (event.altKey) return;
            const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
            if (!(link instanceof HTMLAnchorElement) || link.hasAttribute("download")) return;
            if (link.target && link.target !== "_self") return;
            const url = new URL(link.href, window.location.href);
            if (url.origin !== window.location.origin) return;
            const path = `${url.pathname}${url.search}${url.hash}`;
            const target = browserNavigationTarget(path).routerPath;
            if (target !== null && target.split("#", 1)[0] === `${pathname}${search}`) return;
            event.preventDefault();
            requestLeave(() => platform.open({ kind: "url", url: path }), { scope });
        },
        [platform, scope, pathname, search],
    );
}

/**
 * Chrome made of links (the web header), guarded: a link in it that would leave a page with
 * unsaved changes asks first (`useLeaveGuardLinks`). Lays out as if it were not there.
 */
export function LeaveGuardedLinks({ children }: { children: ReactNode }) {
    const guard = useLeaveGuardLinks();
    return (
        <div style={{ display: "contents" }} onClickCapture={guard}>
            {children}
        </div>
    );
}
